import type { AnnotationSet } from "../../api/threads/liveMessages";
import { isJsonObject } from "../../api/http/jsonDecoding";
import type { UiError } from "../../errors/uiError";

/**
 * Editing an annotation set in a review (W-11, v1's annotation review): add a span, change
 * a span's label, remove a span. Moving a span is removing it and adding the right text,
 * as in v1. Each annotation is named by its index in the set, as the span view names it
 * (`placeAnnotations`) and as the server names it in its problems.
 */

type Annotation = AnnotationSet["annotations"][number];

/** A span the person selected and labelled, already in the server's offsets (`documentOffsetsOf`). */
export interface NewAnnotation {
  readonly segment: string;
  readonly start: number;
  readonly end: number;
  readonly quote: string;
  readonly label: string;
}

/** The set with the new annotation last: its index is the set's former length. It has no note. */
export function withAnnotation(set: AnnotationSet, added: NewAnnotation): AnnotationSet {
  const annotation: Annotation = { ...added, note: "" };
  return { ...set, annotations: [...set.annotations, annotation] };
}

/** The set with annotation `index` under another label; its span and note stay. */
export function withLabel(set: AnnotationSet, index: number, label: string): AnnotationSet {
  return { ...set, annotations: set.annotations.map((annotation, at) => (at === index ? { ...annotation, label } : annotation)) };
}

export function withoutAnnotation(set: AnnotationSet, index: number): AnnotationSet {
  return { ...set, annotations: set.annotations.filter((_annotation, at) => at !== index) };
}

/** Whether two sets hold the same annotations in the same order: an unchanged review confirms. */
export function sameAnnotations(left: AnnotationSet, right: AnnotationSet): boolean {
  return (
    left.annotations.length === right.annotations.length &&
    left.annotations.every((annotation, index) => {
      const other = right.annotations[index];
      return (
        other !== undefined &&
        annotation.segment === other.segment &&
        annotation.start === other.start &&
        annotation.end === other.end &&
        annotation.quote === other.quote &&
        annotation.label === other.label &&
        annotation.note === other.note
      );
    })
  );
}

/** Why the server refused an answer's annotations (`output_invalid`, `details.problems`). */
export interface AnswerProblems {
  /** Each refused annotation's reasons, by its index in the submitted set. */
  readonly byIndex: ReadonlyMap<number, readonly string[]>;
  /** Reasons about the set as a whole. */
  readonly general: readonly string[];
}

export const NO_ANSWER_PROBLEMS: AnswerProblems = { byIndex: new Map(), general: [] };

/**
 * The problems of a refused answer. The checks name an annotation as
 * `{ index, reason }`, the set as `{ index: null, reason }`; the response checks send plain
 * reasons, which the error's own message already states.
 */
export function answerProblems(error: UiError): AnswerProblems {
  if (error.kind !== "problem" || error.problem.code !== "output_invalid") {
    return NO_ANSWER_PROBLEMS;
  }
  const problems: unknown = error.problem.details["problems"];
  const byIndex = new Map<number, string[]>();
  const general: string[] = [];
  for (const problem of Array.isArray(problems) ? problems : []) {
    if (!isJsonObject(problem)) {
      continue;
    }
    const { index, reason } = problem;
    if (typeof reason !== "string") {
      continue;
    }
    if (typeof index === "number" && Number.isInteger(index)) {
      byIndex.set(index, [...(byIndex.get(index) ?? []), reason]);
    } else {
      general.push(reason);
    }
  }
  return { byIndex, general };
}
